import React from 'react';
import { wrapComponent } from 'utils/framework';
import BaseClass from 'components/BaseClass';
import PropTypes from 'prop-types';
import { colors } from 'style/config';
import { Flex } from 'components/ui';

const DOT_SIZE = 6;
const SMALL_DOT_SIZE = 4;
const DOT_MARGIN = 4;
const SMALL_DOT_MARGIN = 3;
const HERO_DOTS_MAX_VISIBLE = 6;
const HERO_ACTIVE_DOT_WIDTH = 24;

class Dots extends BaseClass {
    state = {
        scrolledIndex: 0,
        selectedIndex: 0
    };

    rootRef = React.createRef();

    componentDidUpdate(prevProps) {
        if (!this.props.isHeroMediaDots || prevProps.qty === this.props.qty) {
            return;
        }

        const qty = this.getHeroQtyValue();

        if (qty <= 0) {
            if (this.state.selectedIndex !== 0 || this.state.scrolledIndex !== 0) {
                this.setState({
                    selectedIndex: 0,
                    scrolledIndex: 0
                });
            }

            return;
        }

        const maxIndex = qty - 1;
        const nextSelectedIndex = Math.min(this.state.selectedIndex, maxIndex);
        const visibleCount = Math.min(HERO_DOTS_MAX_VISIBLE, qty);
        const maxStart = Math.max(0, qty - visibleCount);
        const nextScrolledIndex = Math.min(this.state.scrolledIndex, maxStart);

        if (nextSelectedIndex !== this.state.selectedIndex || nextScrolledIndex !== this.state.scrolledIndex) {
            this.setState({
                selectedIndex: nextSelectedIndex,
                scrolledIndex: nextScrolledIndex
            });
        }
    }

    getHeroQtyValue = () => {
        const { qty } = this.props;

        return Array.isArray(qty) ? qty.length : qty || 0;
    };

    getHeroWindowStart = selectedIndex => {
        const qty = this.getHeroQtyValue();

        if (qty <= HERO_DOTS_MAX_VISIBLE) {
            return 0;
        }

        if (selectedIndex <= 2) {
            return 0;
        }

        if (selectedIndex >= qty - 3) {
            return qty - HERO_DOTS_MAX_VISIBLE;
        }

        const isForward = selectedIndex >= this.state.selectedIndex;
        const preferredStart = isForward ? selectedIndex - 3 : selectedIndex - 2;

        return Math.max(0, Math.min(preferredStart, qty - HERO_DOTS_MAX_VISIBLE));
    };

    handleClick = (e, index, preventCallback) => {
        if (this.props.isHeroMediaDots) {
            this.setState(
                {
                    selectedIndex: index,
                    scrolledIndex: this.getHeroWindowStart(index)
                },
                () => {
                    if (this.props.onClick && !preventCallback) {
                        this.props.onClick(e, index);
                    }
                }
            );

            return;
        }

        const { scrolledIndex } = this.state;
        const { qty, dotsShown } = this.props;
        const newScrollIndex =
            index <= scrolledIndex
                ? Math.max(index - 1, 0)
                : index >= scrolledIndex + dotsShown - 1
                    ? Math.min(index - dotsShown + 2, qty - dotsShown)
                    : scrolledIndex;

        this.setState(
            {
                selectedIndex: index,
                scrolledIndex: newScrollIndex
            },
            () => {
                this.rootRef && this.rootRef.current.scrollTo(newScrollIndex * (DOT_SIZE + DOT_MARGIN), 0);

                if (this.props.onClick && !preventCallback) {
                    this.props.onClick(e, index);
                }
            }
        );
    };

    scrollTo = index => {
        if (index !== this.state.selectedIndex) {
            this.handleClick(null, index, true);
        }
    };

    shouldScrollLeft = index => {
        const { scrolledIndex } = this.state;

        return !!scrolledIndex && index === scrolledIndex;
    };

    shouldScrollRight = index => {
        const { scrolledIndex } = this.state;
        const { qty, dotsShown } = this.props;

        return qty > scrolledIndex + dotsShown && index === scrolledIndex + 4;
    };

    getDotStyle = index => {
        const { selectedIndex } = this.state;
        const scrollable = this.shouldScrollLeft(index) || this.shouldScrollRight(index);

        return {
            width: scrollable ? SMALL_DOT_SIZE : null,
            height: scrollable ? SMALL_DOT_SIZE : null,
            margin: scrollable ? `0 ${SMALL_DOT_MARGIN}px` : null,
            backgroundColor: index === selectedIndex ? colors.gray : null
        };
    };

    getHeroVisibleIndices = () => {
        const qty = this.getHeroQtyValue();
        const { scrolledIndex } = this.state;
        const visibleCount = Math.min(HERO_DOTS_MAX_VISIBLE, qty);
        const maxStart = Math.max(0, qty - visibleCount);
        const clampedScrolledIndex = Math.min(scrolledIndex, maxStart);

        return Array.from({ length: visibleCount }, (_item, idx) => clampedScrolledIndex + idx);
    };

    getHeroDotType = (itemIndex, isLeftEdge, isRightEdge) => {
        const { dotTypes = [] } = this.props;

        if (isLeftEdge && itemIndex > 0) {
            return dotTypes[itemIndex - 1];
        }

        if (isRightEdge && itemIndex < dotTypes.length - 1) {
            return dotTypes[itemIndex + 1];
        }

        return dotTypes[itemIndex];
    };

    getHeroIndicatorStyle = ({ isActive, dotType, isEdge }) => {
        if (isActive) {
            return styles.heroActiveDot;
        }

        const isVideo = dotType === 'VIDEO';

        if (isVideo) {
            return isEdge ? styles.heroVideoEdgeDot : styles.heroVideoDot;
        }

        return isEdge ? styles.heroImageEdgeDot : styles.heroImageDot;
    };

    renderHeroDots = () => {
        const qty = this.getHeroQtyValue();
        const { selectedIndex, scrolledIndex } = this.state;
        const visibleIndices = this.getHeroVisibleIndices();
        const hasLeftHidden = qty > HERO_DOTS_MAX_VISIBLE && scrolledIndex > 0;
        const hasRightHidden = qty > HERO_DOTS_MAX_VISIBLE && scrolledIndex + HERO_DOTS_MAX_VISIBLE < qty;

        return (
            <Flex
                aria-hidden
                ref={this.rootRef}
                css={styles.heroRoot}
            >
                {visibleIndices.map((itemIndex, slotIndex) => {
                    const isLeftEdge = hasLeftHidden && slotIndex === 0;
                    const isRightEdge = hasRightHidden && slotIndex === visibleIndices.length - 1;
                    const isActive = itemIndex === selectedIndex;
                    const dotType = this.getHeroDotType(itemIndex, isLeftEdge, isRightEdge);

                    return (
                        <button
                            type='button'
                            key={itemIndex.toString()}
                            tabIndex={-1}
                            onClick={e => this.handleClick(e, itemIndex)}
                            css={styles.heroDotButton}
                        >
                            <span
                                css={[styles.heroIndicatorBase, this.getHeroIndicatorStyle({ isActive, dotType, isEdge: isLeftEdge || isRightEdge })]}
                            />
                        </button>
                    );
                })}
            </Flex>
        );
    };

    render() {
        const { qty, dotsShown, isHeroMediaDots } = this.props;

        if (isHeroMediaDots) {
            return this.renderHeroDots();
        }

        return (
            <div
                aria-hidden
                ref={this.rootRef}
                css={[styles.root, { width: dotsShown * (DOT_SIZE + DOT_MARGIN) }]}
            >
                <div
                    css={[
                        styles.container,
                        qty < dotsShown && {
                            justifyContent: 'flex-end'
                        }
                    ]}
                >
                    {new Array(qty).fill().map((item, index) => (
                        <button
                            type='button'
                            key={index.toString()}
                            tabIndex={-1}
                            onClick={e => this.handleClick(e, index)}
                            css={styles.dot}
                            style={this.getDotStyle(index)}
                        />
                    ))}
                </div>
            </div>
        );
    }
}

Dots.propTypes = {
    qty: PropTypes.oneOfType([PropTypes.array, PropTypes.number]),
    dotsShown: PropTypes.number,
    isHeroMediaDots: PropTypes.bool,
    dotTypes: PropTypes.arrayOf(PropTypes.oneOf(['IMAGE', 'VIDEO'])),
    onClick: PropTypes.func
};

const styles = {
    root: {
        overflow: 'hidden',
        scrollBehavior: 'smooth'
    },
    container: {
        display: 'flex',
        alignItems: 'center'
    },
    dot: {
        flexShrink: 0,
        backgroundColor: '#e7e7e7',
        borderRadius: 99999,
        margin: `0 ${DOT_MARGIN / 2}px`,
        width: DOT_SIZE,
        height: DOT_SIZE
    },
    heroRoot: {
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6
    },
    heroDotButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        margin: 0,
        border: 0,
        background: 'transparent',
        lineHeight: 0
    },
    heroIndicatorBase: {
        transition: 'width 350ms ease, height 350ms ease, border-color 350ms ease, border-left-width 350ms ease, background-color 350ms ease',
        transformOrigin: 'center center'
    },
    heroActiveDot: {
        width: HERO_ACTIVE_DOT_WIDTH,
        height: DOT_SIZE,
        borderRadius: 20,
        backgroundColor: colors.gray
    },
    heroImageDot: {
        width: DOT_SIZE,
        height: DOT_SIZE,
        borderRadius: 99999,
        backgroundColor: colors.midGray
    },
    heroImageEdgeDot: {
        width: SMALL_DOT_SIZE,
        height: SMALL_DOT_SIZE,
        borderRadius: 99999,
        backgroundColor: colors.midGray
    },
    heroVideoDot: {
        width: 0,
        height: 0,
        borderTop: `${DOT_SIZE / 2}px solid transparent`,
        borderBottom: `${DOT_SIZE / 2}px solid transparent`,
        borderLeft: `${DOT_SIZE}px solid ${colors.midGray}`
    },
    heroVideoEdgeDot: {
        width: 0,
        height: 0,
        borderTop: `${SMALL_DOT_SIZE / 2}px solid transparent`,
        borderBottom: `${SMALL_DOT_SIZE / 2}px solid transparent`,
        borderLeft: `${SMALL_DOT_SIZE}px solid ${colors.midGray}`
    }
};

export default wrapComponent(Dots, 'Dots');
